import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
    FaCheckCircle,
    FaExclamationCircle,
    FaExclamationTriangle,
    FaInfoCircle,
    FaTrashAlt,
    FaUndo,
    FaTimes,
} from 'react-icons/fa';

import '../css/feedback.css';

/** Mount a transient component on document.body and return a disposer. */
function mountTransient(renderWithClose) {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    let closed = false;
    const close = () => {
        if (closed) return;
        closed = true;
        // Defer unmount so we never unmount during React's render/commit phase.
        setTimeout(() => {
            root.unmount();
            if (container.parentNode) container.parentNode.removeChild(container);
        }, 0);
    };

    root.render(renderWithClose(close));
    return close;
}

const ALERT_TITLES = {
    success: 'Success',
    error: 'Something went wrong',
    warning: 'Please check',
    info: 'Notice',
};

function inferAlertType(message) {
    const text = String(message || '').toLowerCase();
    const isFailure = /(fail|error|could not|cannot|can't|unable|not found|not authorized|expired|denied)/.test(
        text
    );
    const isSuccess = /(success|successfully|saved|created|updated|deleted|added|activated|deactivated|uploaded|assigned)/.test(
        text
    );
    if (isSuccess && !isFailure) return 'success';
    if (isFailure) return 'error';
    if (/(required|please |invalid|missing|select |enter |must |already)/.test(text)) {
        return 'warning';
    }
    return 'info';
}

function variantIcon(variant) {
    if (variant === 'success') return <FaCheckCircle />;
    if (variant === 'error') return <FaExclamationCircle />;
    if (variant === 'danger') return <FaTrashAlt />;
    if (variant === 'warning' || variant === 'confirm') return <FaExclamationTriangle />;
    return <FaInfoCircle />;
}

function lockBodyScroll() {
    const body = document.body;
    if (!body) return () => {};
    const previous = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
        body.style.overflow = previous;
    };
}

function FeedbackChrome({
    variant = 'info',
    title,
    message,
    role = 'alertdialog',
    onBackdrop,
    children,
    actionRef,
}) {
    const onBackdropRef = useRef(onBackdrop);
    onBackdropRef.current = onBackdrop;

    useEffect(() => {
        if (actionRef?.current) actionRef.current.focus();
        const unlock = lockBodyScroll();
        const onKey = (e) => {
            if (e.key === 'Escape') onBackdropRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            unlock();
        };
    }, [actionRef]);

    return (
        <div className="feedback-overlay" onMouseDown={onBackdrop}>
            <div
                className={`feedback-modal feedback-modal--${variant}`}
                role={role}
                aria-modal="true"
                aria-labelledby="feedback-modal-title"
                aria-describedby={message ? 'feedback-modal-message' : undefined}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="feedback-modal__accent" aria-hidden="true" />
                <div className={`feedback-modal__icon feedback-modal__icon--${variant}`}>
                    {variantIcon(variant)}
                </div>
                {title ? (
                    <h3 id="feedback-modal-title" className="feedback-modal__title">
                        {title}
                    </h3>
                ) : null}
                {message ? (
                    <p id="feedback-modal-message" className="feedback-modal__message">
                        {message}
                    </p>
                ) : null}
                <div className="feedback-modal__actions">{children}</div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Confirm dialog                                                      */
/* ------------------------------------------------------------------ */

function ConfirmDialog({
    title,
    message,
    confirmLabel,
    cancelLabel,
    danger,
    onResolve,
}) {
    const confirmBtnRef = useRef(null);

    return (
        <FeedbackChrome
            variant={danger ? 'danger' : 'confirm'}
            title={title}
            message={message}
            onBackdrop={() => onResolve(false)}
            actionRef={confirmBtnRef}
        >
                    <button
                        type="button"
                        className="feedback-btn feedback-btn--ghost"
                        onClick={() => onResolve(false)}
                    >
                        {cancelLabel || 'Cancel'}
                    </button>
                    <button
                        ref={confirmBtnRef}
                        type="button"
                        className={`feedback-btn ${danger ? 'feedback-btn--danger' : 'feedback-btn--primary'}`}
                        onClick={() => onResolve(true)}
                    >
                        {confirmLabel || 'Confirm'}
                    </button>
        </FeedbackChrome>
    );
}

/* ------------------------------------------------------------------ */
/* Alert / notification dialog                                         */
/* ------------------------------------------------------------------ */

function AlertDialog({ title, message, type, okLabel, onClose }) {
    const okBtnRef = useRef(null);

    return (
        <FeedbackChrome
            variant={type}
            title={title}
            message={message}
            onBackdrop={onClose}
            actionRef={okBtnRef}
        >
            <button
                ref={okBtnRef}
                type="button"
                className="feedback-btn feedback-btn--primary"
                onClick={onClose}
            >
                {okLabel || 'OK'}
            </button>
        </FeedbackChrome>
    );
}

const alertQueue = [];
let alertBusy = false;

function drainAlertQueue() {
    if (alertBusy || alertQueue.length === 0) return;
    const next = alertQueue.shift();
    alertBusy = true;
    mountTransient((dispose) => {
        const handle = () => {
            dispose();
            alertBusy = false;
            next.resolve();
            drainAlertQueue();
        };
        return (
            <AlertDialog
                title={next.title}
                message={next.message}
                type={next.type}
                okLabel={next.okLabel}
                onClose={handle}
            />
        );
    });
}

/**
 * Centered app notification modal. Sequential calls are queued.
 * @returns {Promise<void>}
 */
export function showAppAlert(message, options = {}) {
    const text = message == null ? '' : String(message);
    const type = options.type || inferAlertType(text);
    return new Promise((resolve) => {
        alertQueue.push({
            message: text,
            type,
            title: options.title || ALERT_TITLES[type] || ALERT_TITLES.info,
            okLabel: options.okLabel,
            resolve,
        });
        drainAlertQueue();
    });
}

let notificationsInstalled = false;

/** Route native `alert()` through the centered app modal. */
export function installAppNotifications() {
    if (notificationsInstalled || typeof window === 'undefined') return;
    notificationsInstalled = true;
    window.alert = (message) => {
        showAppAlert(message);
    };
}

/**
 * Promise-based confirmation dialog (drop-in, nicer replacement for window.confirm).
 * @returns {Promise<boolean>} resolves true if confirmed.
 */
export function confirmAction({
    title = 'Are you sure?',
    message = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
} = {}) {
    return new Promise((resolve) => {
        mountTransient((dispose) => {
            const handle = (result) => {
                dispose();
                resolve(result);
            };
            return (
                <ConfirmDialog
                    title={title}
                    message={message}
                    confirmLabel={confirmLabel}
                    cancelLabel={cancelLabel}
                    danger={danger}
                    onResolve={handle}
                />
            );
        });
    });
}

/* ------------------------------------------------------------------ */
/* Undo toast (Gmail-style deferred action)                            */
/* ------------------------------------------------------------------ */

function UndoToast({ message, undoLabel, duration, onUndo, onCommit, onDone }) {
    const [remaining, setRemaining] = useState(Math.ceil(duration / 1000));
    const settledRef = useRef(false);
    const settleRef = useRef(() => {});

    useEffect(() => {
        const startedAt = Date.now();
        const tick = setInterval(() => {
            const left = Math.max(0, duration - (Date.now() - startedAt));
            setRemaining(Math.ceil(left / 1000));
        }, 250);

        const settle = (kind) => {
            if (settledRef.current) return;
            settledRef.current = true;
            clearTimeout(timer);
            clearInterval(tick);
            if (kind === 'undo') {
                Promise.resolve()
                    .then(() => onUndo && onUndo())
                    .finally(() => onDone());
            } else {
                Promise.resolve()
                    .then(() => onCommit && onCommit())
                    .catch((err) => console.error('Undo toast commit failed:', err))
                    .finally(() => onDone());
            }
        };
        settleRef.current = settle;

        const timer = setTimeout(() => settle('commit'), duration);

        return () => {
            clearTimeout(timer);
            clearInterval(tick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const settle = (kind) => settleRef.current(kind);

    return (
        <div className="feedback-toast" role="status" aria-live="polite">
            <span className="feedback-toast__message">{message}</span>
            <button
                type="button"
                className="feedback-toast__undo"
                onClick={() => settle('undo')}
            >
                <FaUndo aria-hidden="true" /> {undoLabel || 'Undo'} ({remaining})
            </button>
            <button
                type="button"
                className="feedback-toast__close"
                aria-label="Dismiss"
                onClick={() => settle('commit')}
            >
                <FaTimes />
            </button>
        </div>
    );
}

/**
 * Show a toast with an Undo action. The destructive `onCommit` runs ONLY after the
 * toast expires (or is dismissed) without an Undo, giving the user a real window to
 * change their mind. If Undo is pressed, `onUndo` runs and `onCommit` never fires.
 *
 * Typical usage (optimistic delete): remove the row from UI immediately, pass a
 * restore function as `onUndo`, and the real API delete as `onCommit`.
 */
export function showUndoToast({
    message = 'Deleted',
    undoLabel = 'Undo',
    duration = 5000,
    onUndo,
    onCommit,
} = {}) {
    mountTransient((dispose) => (
        <UndoToast
            message={message}
            undoLabel={undoLabel}
            duration={duration}
            onUndo={onUndo}
            onCommit={onCommit}
            onDone={dispose}
        />
    ));
}
